import { useState, useCallback } from 'react';
import { SUBMIT_PROVIDER_LEAD, getProviderLeadConfig } from '../graphql/mutations/providerLeadMutations';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Detects if a string looks like an email or a phone number.
 * @returns {'email' | 'phone' | null}
 */
function parseReferrerContact(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return { type: null, value: null };
  if (EMAIL_REGEX.test(trimmed)) return { type: 'email', value: trimmed };
  return { type: 'phone', value: trimmed };
}

/**
 * Builds the input object for SubmitProviderLead from form data.
 * Maps: studioName, providerEmail (required), providerPhone, referrerName,
 * referrerEmail or referrerPhone (from one optional field), source.
 */
export function buildSubmitProviderLeadInput(formData) {
  const {
    studioName,
    providerEmail,
    providerPhone,
    referrerName,
    referrerContact,
  } = formData;

  const referrer = parseReferrerContact(referrerContact);

  const input = {
    studioName: (studioName || '').trim(),
    providerEmail: (providerEmail || '').trim().toLowerCase(),
    providerPhone: providerPhone ? String(providerPhone).trim() || null : null,
    referrerName: referrerName ? String(referrerName).trim() || null : null,
    referrerEmail: referrer?.type === 'email' ? referrer.value : null,
    referrerPhone: referrer?.type === 'phone' ? referrer.value : null,
    source: 'website_referral',
  };

  return input;
}

/**
 * Hook to submit the "Studio empfehlen" form to the public GraphQL API.
 * Uses fetch with x-api-key (no Cognito).
 *
 * @returns {{
 *   submitLead: (formData: Object) => Promise<{ success: boolean, message?: string, leadID?: string }>,
 *   isSubmitting: boolean,
 *   error: string | null,
 *   clearError: () => void
 * }}
 */
export function useSubmitProviderLead() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const submitLead = useCallback(async (formData) => {
    const { endpoint, apiKey } = getProviderLeadConfig();

    if (!endpoint || !apiKey) {
      setError('API ist nicht konfiguriert. Bitte Endpoint und API-Key prüfen.');
      return { success: false, message: 'API nicht konfiguriert' };
    }

    const input = buildSubmitProviderLeadInput(formData);

    if (!input.studioName) {
      setError('Studioname ist ein Pflichtfeld.');
      return { success: false, message: 'Studioname ist ein Pflichtfeld.' };
    }
    if (!input.providerEmail) {
      setError('Bitte eine gültige E-Mail-Adresse angeben.');
      return { success: false, message: 'E-Mail fehlt.' };
    }
    if (!EMAIL_REGEX.test(input.providerEmail)) {
      setError('Bitte eine gültige E-Mail-Adresse angeben.');
      return { success: false, message: 'Ungültige E-Mail.' };
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({
          query: SUBMIT_PROVIDER_LEAD,
          variables: { input },
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        setError('Netzwerkfehler. Bitte später erneut versuchen.');
        return { success: false, message: 'Netzwerkfehler' };
      }

      const gqlErrors = Array.isArray(result.errors) ? result.errors : [];
      const payload = result.data?.submitProviderLead;

      // AppSync returns 200 + errors when e.g. response type fields are not authorized for API key.
      if (gqlErrors.length > 0 && !payload) {
        const unauthorized = gqlErrors.some(
          (e) =>
            e?.errorType === 'Unauthorized' ||
            (typeof e?.message === 'string' && e.message.includes('Not Authorized')),
        );
        const message = unauthorized
          ? 'Formular-API: Antwort ist serverseitig nicht freigegeben (AppSync Auth). Bitte Backend-Team informieren.'
          :(gqlErrors[0]?.message || 'Serverfehler. Bitte später erneut versuchen.');
        setError(message);
        return { success: false, message };
      }

      if (payload?.success) {
        setError(null);
        return {
          success: true,
          message: payload.message || 'Vielen Dank! Wir melden uns in Kürze.',
          leadID: payload.leadID,
        };
      }

      const message = payload?.message || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
      setError(message);
      return { success: false, message };
    } catch (err) {
      const message = err?.message || 'Etwas ist schiefgelaufen. Bitte versuche es erneut.';
      setError(message);
      return { success: false, message };
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return {
    submitLead,
    isSubmitting,
    error,
    clearError,
  };
}

export default useSubmitProviderLead;
